import { useTranslation } from "../../hooks/useTranslation";
import { Container } from "react-bootstrap";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

/**
 * Compact, home‑page price teaser.
 *
 * • Shows three items from the chosen section (default: "adult").
 * • Adds a headline and a “See full list” button that routes to "/arlista" via <NavLink>.
 * • Leverages existing glass‑morphism classes—no extra CSS needed.
 *
 * Props ──────────────────────────────────────────────────────────
 * @param {string}  sectionKey   Which translation section to render
 * @param {Array}   items        Optional custom items (overrides sectionKey)
 * @param {string}  title        Custom title text (defaults to t("pricelist.shortTitle"))
 * @param {number}  motionOrder  Stagger index for fade‑up animation
 */
export default function PriceListShort({
    sectionKey = "adult",
    items,
    title,
    motionOrder = 0
}) {
    const t = useTranslation();
    const sections = t("pricelist.sections");

    // Resolve data and UI texts ───────────────────────────────────
    const listItems = (items || sections?.[sectionKey]?.items || []).slice(0, 3);
    const headline = t("home.pricelist.title");
    const ctaLabel = t("home.pricelist.viewFull");

    // Basic fade‑up animation (same as long version) ─────────────
    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        show: { opacity: 1, y: 0, transition: { delay: motionOrder * 0.05, duration: 0.4 } }
    };

    return (
        <section id="pricelistShort" className="price-list-dark-wrapper">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-20px" }}
            >
                <Container className="servicesShortContainer">
                    <div className="zoom-wrapper">
                        <div className="glass-wrapper shadow text-center">
                            {/* Title */}
                            <h3 className="pricelistShortTitle mb-5 mt-4">{headline}</h3>

                            {/* Price list */}
                            <ul className="list-unstyled mb-0">
                                {listItems.map(({ name, price, desc }, idx) => (
                                    <li key={idx} className="price-item border-bottom mb-4 pb-4">
                                        <div className="d-flex justify-content-between fw-semibold">
                                            <span>{name}</span>
                                            <span className="text-warning">{price}</span>
                                        </div>
                                        {desc && <small className="desc d-block mt-1 text-start">{desc}</small>}
                                    </li>
                                ))}
                            </ul>
                            {/* CTA button */}
                            <div className="mb-4">
                                <NavLink to="/arlista" className="priceListShortBtn btn btn-outline-warning p-3 mt-4">
                                    {ctaLabel}
                                </NavLink>
                            </div>
                        </div>
                    </div>
                </Container>
            </motion.div>
        </section>
    );
}
